// Staff surface — shell only. People owns unified-account membership UI and
// the bounded claim path for legacy club-managed work records;
// Roles and Groups consume canonical RBAC; Experience claims manages explicit
// portable teaching evidence without transferring club-owned coach profiles.
// The orgId comes from useMyOrg and the active facility from the FacilityContext
// selection (useResolvedFacility); org People remains usable without a facility.
// The tab internals live in src/components/staff/*.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { RolesTab } from "@/components/staff/roles-tab";
import { GroupsTab } from "@/components/staff/groups-tab";
import { PeopleTab } from "@/components/staff/people-tab";
import { ExperienceClaimsTab } from "@/components/staff/experience-claims-tab";
import { PeopleCommandBoundary } from "./people-command-lifetime";
import { financeApi } from "@bw/shared/api";
import { useFinanceContext } from "@/hooks/use-finance-context";

export function StaffPage() {
  const [activeTab, setActiveTab] = useState("people");
  const orgView = useMyOrg();
  const orgId = orgView.org?.org_id;
  const finance = useFinanceContext(
    orgId,
    financeApi,
    orgView.org?.finance_capability === "allowed",
  );
  const staffManagementAllowed = orgView.org?.can_manage_staff === true;
  const financeAllowed = finance.context?.can_manage_finance_access === true;
  const financeCapability = orgView.org?.finance_capability;
  const financeDiscoveryPending = !staffManagementAllowed &&
    financeCapability === "allowed" && finance.pending;
  const financeDiscoveryUnavailable = !staffManagementAllowed &&
    (financeCapability === "unavailable" ||
      (financeCapability === "allowed" && finance.query.isError));
  const {
    facility,
    facilityId,
    facilities,
  } = useResolvedFacility();

  const loading = orgView.kind === "loading" || financeDiscoveryPending;

  return (
    <PeopleCommandBoundary
      key={`${orgId ?? "no-organization"}:${
        orgView.staffCapabilityPending === true ||
        orgView.org?.can_manage_staff === true
          ? "authorized-lifetime"
          : "closed-lifetime"
      }`}
      allowed={orgView.org?.can_manage_staff === true}
      pending={orgView.staffCapabilityPending === true}
    >
      <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 } }}>
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
            Team
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Manage members and their access to your organization.
          </Typography>
        </Box>

        {loading ? (
          <Stack
            direction="row"
            role="status"
            spacing={1.5}
            sx={{ alignItems: "center", justifyContent: "center", py: 5 }}
          >
            <CircularProgress size={20} />
            <Typography color="text.secondary" variant="body2">
              Loading…
            </Typography>
          </Stack>
        ) : orgView.kind === "error" || !orgId ? (
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Alert severity="error">
              {orgView.kind === "error"
                ? orgView.message
                : "Select a club before managing staff."}
            </Alert>
            <Button component={Link} to="/inquiries" size="small">
              Go to Inbox
            </Button>
          </Stack>
        ) : financeDiscoveryUnavailable ? (
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Alert severity="warning">
              Team access is temporarily unavailable. Refresh before changing
              staff or financial access.
            </Alert>
            <Button component={Link} to="/inquiries" size="small">
              Go to Inbox
            </Button>
          </Stack>
        ) : !staffManagementAllowed && !financeAllowed ? (
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <Alert severity="info">
              Team requires club-wide staff-management access. Ask your club
              owner if you need to manage staff.
            </Alert>
            <Button component={Link} to="/inquiries" size="small">
              Go to Inbox
            </Button>
          </Stack>
        ) : (
          <Box>
            <Tabs
              allowScrollButtonsMobile
              aria-label="Staff sections"
              onChange={(_, value: string) => setActiveTab(value)}
              scrollButtons="auto"
              value={activeTab}
              variant="scrollable"
            >
              <Tab id="staff-tab-people" label="People" value="people" />
              {staffManagementAllowed && <Tab id="staff-tab-roles" label="Roles" value="roles" />}
              {staffManagementAllowed && <Tab id="staff-tab-groups" label="Groups" value="groups" />}
              {staffManagementAllowed && <Tab
                id="staff-tab-experience"
                label="Experience claims"
                value="experience"
              />}
            </Tabs>

            {staffManagementAllowed && activeTab === "roles" ? (
              <Box aria-labelledby="staff-tab-roles" role="tabpanel">
                <RolesTab orgId={orgId} />
              </Box>
            ) : null}

            {activeTab === "people" ? (
              <Box aria-labelledby="staff-tab-people" role="tabpanel">
                <PeopleTab
                  orgId={orgId}
                  facilityId={facilityId}
                  facilityName={facility?.name ?? null}
                  facilities={facilities}
                  financeContext={finance.context}
                  staffManagementAllowed={staffManagementAllowed}
                />
              </Box>
            ) : null}

            {staffManagementAllowed && activeTab === "groups" ? (
              <Box aria-labelledby="staff-tab-groups" role="tabpanel">
                <GroupsTab orgId={orgId} />
              </Box>
            ) : null}
            {staffManagementAllowed && activeTab === "experience" ? (
              <Box aria-labelledby="staff-tab-experience" role="tabpanel">
                <ExperienceClaimsTab orgId={orgId} />
              </Box>
            ) : null}
          </Box>
        )}
      </Stack>
    </PeopleCommandBoundary>
  );
}
